import React, { useState, useEffect } from "react";
import { Form, InputNumber, Button, Card, Typography, Row, Col } from "antd";

const { Title, Text } = Typography;

const WithLoanRentalYieldCalculator = () => {
  const [rentalYield, setRentalYield] = useState(null);
  const [investmentQuality, setInvestmentQuality] = useState("");
  const [riskWarning, setRiskWarning] = useState("");
  const [currency, setCurrency] = useState("$");

  const formatNumber = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const parseNumber = (value) => {
    return value ? value.replace(/\s/g, "") : "";
  };

  useEffect(() => {
        fetch("https://ipapi.co/json/")
          .then((res) => res.json())
          .then((data) => {
            const countryCode = data.country_code; // e.g., ZA, US
            setCurrency(getCurrencyCode(countryCode));
          })
          .catch((err) => {
            console.error("Error fetching currency:", err);
            setCurrency("$"); // Fallback
          });
      }, []);
    
      const getCurrencyCode = (countryCode) => {
        const currencyMap = {
          ZA: "R",   // South Africa
          US: "$",   // United States
          GB: "£",   // United Kingdom
          NG: "₦",   // Nigeria
          KE: "KES", // Kenya
          IN: "₹",   // India
        };
        return currencyMap[countryCode] || "$";
      };

  const calculateRentalYield = (values) => {
    const { monthlyRent, annualExpenses, propertyPrice, vacancyRate, loanAmount, interestRate, loanTerm } = values;
    
    const annualRentIncome = monthlyRent * 12 * (1 - vacancyRate / 100);
    const monthlyInterestRate = interestRate / 100 / 12;
    const totalMonths = loanTerm * 12;
    
    let mortgagePayment = 0;
    if (monthlyInterestRate > 0) {
      mortgagePayment = loanAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, totalMonths)) / (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);
    } else {
      mortgagePayment = loanAmount / totalMonths;
    }
    
    const annualMortgagePayments = mortgagePayment * 12;
    const netRentalYield = ((annualRentIncome - annualExpenses - annualMortgagePayments) / propertyPrice) * 100;
    setRentalYield(netRentalYield.toFixed(2));

    if (netRentalYield < 4) {
      setInvestmentQuality("🚨 Poor Investment");
      setRiskWarning("Low returns. Property costs and mortgage may be too high compared to rent.");
    } else if (netRentalYield >= 4 && netRentalYield <= 6) {
      setInvestmentQuality("🟡 Moderate Investment");
      setRiskWarning("Safe and stable, but may have lower cash flow.");
    } else if (netRentalYield > 6 && netRentalYield <= 10) {
      setInvestmentQuality("🟢 Good Investment");
      setRiskWarning("Balanced profitability and manageable risks.");
    } else {
      setInvestmentQuality("🔥 High Yield - Potentially Risky");
      setRiskWarning("High rental yield can indicate potential risks such as high tenant turnover, maintenance issues, or low long-term appreciation. Research carefully before investing.");
    }
  };

  return (
    <Card title="Rental Yield Calculator with Loan" style={{ maxWidth: 800, margin: "auto", marginTop: 20 }}>
      <Row gutter={24}>
        <Col span={15}>
          <Form layout="vertical" onFinish={calculateRentalYield} initialValues={{ monthlyRent: 10000, annualExpenses: 12000, propertyPrice: 1000000, vacancyRate: 5, loanAmount: 800000, interestRate: 10, loanTerm: 20 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={"Monthly Rent Income (" + currency + ")"} name="monthlyRent" rules={[{ required: true, message: "Please enter the monthly rent income" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} step={100} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={"Annual Expenses (" + currency + ")"} name="annualExpenses" rules={[{ required: true, message: "Please enter the annual expenses" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0} step={100} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={"Property Purchase Price (" + currency + ")"} name="propertyPrice" rules={[{ required: true, message: "Please enter the property purchase price" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} step={1000} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Vacancy Rate (%)" name="vacancyRate" rules={[{ required: true, message: "Please enter the estimated vacancy rate" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0} max={100} step={0.1} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={"Loan Amount (" + currency + ")"} name="loanAmount" rules={[{ required: true, message: "Please enter the loan amount" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} step={1000} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Interest Rate (%)" name="interestRate" rules={[{ required: true, message: "Please enter the interest rate" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Loan Term (Years)" name="loanTerm" rules={[{ required: true, message: "Please enter the loan term" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} max={30} step={1} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Calculate Rental Yield
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Card>
  );
};

export default WithLoanRentalYieldCalculator;
